import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import styles from './RightPanel.module.scss';
import CodeSnippet from '../CodeSnippet';
import Spinner from '../Spinner';
import Response from '../Response';
import Authorization from '../Authorization';
import { generateCurlSnippet, generateJavascriptSnippet, generatePythonSnippet, generateRubySnippet, generateCSnippet, generateCppSnippet, generateJavaSnippet, generateShellSnippet, generateNodeSnippet, generatePHPSnippet, generateCSharpSnippet, generateSwiftSnippet, generateHTTPSnippet } from '../../utils/api-parser';

const languageIcons = {
  curl: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 8L8 12L4 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M10 19H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  javascript: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M21 1.5H3C2.17157 1.5 1.5 2.17157 1.5 3V21C1.5 21.8284 2.17157 22.5 3 22.5H21C21.8284 22.5 22.5 21.8284 22.5 21V3C22.5 2.17157 21.8284 1.5 21 1.5Z" fill="#F7DF1E"/>
      <path d="M14.625 15.375C14.3751 15.6565 14.049 15.8649 13.6826 15.9791C13.3162 16.0932 12.9234 16.1088 12.5417 16.0242C12.16 15.9395 11.8041 15.7578 11.512 15.5004C11.2199 15.243 11.0022 14.9208 10.8854 14.5625L9.75 10.875H12L11.25 13.5C11.3323 13.6393 11.451 13.756 11.5938 13.8375C11.7366 13.919 11.8982 13.9625 12.0625 13.9625C12.4583 13.9625 12.75 13.625 12.75 13.125V10.875H15L14.625 15.375Z" fill="black"/>
    </svg>
  ),
  python: (
    <svg width="24" height="24" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M24 44C35.0457 44 44 35.0457 44 24C44 12.9543 35.0457 4 24 4C12.9543 4 4 12.9543 4 24C4 35.0457 12.9543 44 24 44Z" fill="#306998"/>
      <path fillRule="evenodd" clipRule="evenodd" d="M24 31V37H18C16.3431 37 15 35.6569 15 34V31H24ZM24 17V11H30C31.6569 11 33 12.3431 33 14V17H24Z" fill="#FFD43B"/>
      <path d="M15 31V24H21V31H15Z" fill="#FFD43B"/>
      <path d="M27 17V24H33V17H27Z" fill="#FFD43B"/>
      <circle cx="18" cy="20" r="2" fill="#306998"/>
      <circle cx="30" cy="28" r="2" fill="#306998"/>
    </svg>
  ),
  ruby: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19.438 4.312L12 1L4.562 4.312L1 11.25L12 23L23 11.25L19.438 4.312ZM12 3.656L17.25 6.656L12 17.344L6.75 6.656L12 3.656ZM17.969 11.25L12 19.562L6.031 11.25L7.969 8.156L12 10.438L16.031 8.156L17.969 11.25Z" fill="#CC342D"/>
    </svg>
  ),
  c: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14.6289 5.25C12.1641 5.25 10.125 7.14844 10.125 9.75V14.25C10.125 16.8516 12.1641 18.75 14.6289 18.75C17.0938 18.75 19.125 16.8516 19.125 14.25V13.3125H17.25V14.25C17.25 15.7266 16.0898 16.875 14.625 16.875C13.1602 16.875 12 15.7266 12 14.25V9.75C12 8.27344 13.1602 7.125 14.625 7.125C16.0898 7.125 17.25 8.27344 17.25 9.75H19.125V9.60938C19.125 7.14844 17.0938 5.25 14.6289 5.25Z" fill="#A8B9CC"/>
    </svg>
  ),
  cpp: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M13 5.25C10.5352 5.25 8.5 7.14844 8.5 9.75V14.25C8.5 16.8516 10.5352 18.75 13 18.75C15.4648 18.75 17.5 16.8516 17.5 14.25V13.3125H15.625V14.25C15.625 15.7266 14.4648 16.875 13 16.875C11.5352 16.875 10.375 15.7266 10.375 14.25V9.75C10.375 8.27344 11.5352 7.125 13 7.125C14.4648 7.125 15.625 8.27344 15.625 9.75H17.5V9.60938C17.5 7.14844 15.4648 5.25 13 5.25Z" fill="#00599C"/>
      <path d="M19.75 10.75V8.875H17.875V7H16V8.875H14.125V10.75H16V12.625H17.875V10.75H19.75Z" fill="#00599C"/>
      <path d="M19.75 17V15.125H17.875V13.25H16V15.125H14.125V17H16V18.875H17.875V17H19.75Z" fill="#00599C"/>
    </svg>
  ),
  java: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM16 16.5C14.89 16.5 14 15.61 14 14.5V12.5H12V16.5C10.89 16.5 10 15.61 10 14.5V9.5C10 8.39 10.89 7.5 12 7.5H14C15.11 7.5 16 8.39 16 9.5V11.5H18V9.5C19.11 9.5 20 8.61 20 7.5H20C20 6.39 19.11 5.5 18 5.5H6C4.89 5.5 4 6.39 4 7.5C4 8.61 4.89 9.5 6 9.5H8V14.5C8 15.61 7.11 16.5 6 16.5H6C4.89 16.5 4 17.39 4 18.5C4 19.61 4.89 20.5 6 20.5H18C19.11 20.5 20 19.61 20 18.5C20 17.39 19.11 16.5 18 16.5H16Z" fill="#f89820"/>
    </svg>
  ),
  shell: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 8L8 12L4 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M10 19H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  node: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2L1 9L12 22L23 9L12 2Z" fill="#68A063"/>
      <path d="M12 2V22" stroke="white" strokeWidth="2"/>
    </svg>
  ),
  php: (
    <svg width="24" height="24" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="24" cy="24" rx="18" ry="13" fill="#777BB4"/>
      <text x="24" y="24" dominant-baseline="middle" text-anchor="middle" fill="white" font-size="14" font-family="Arial, sans-serif" font-weight="bold">php</text>
    </svg>
  ),
  csharp: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="24" height="24" rx="4" fill="#512BD4"/>
        <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="white" font-size="12" font-family="Arial, sans-serif" font-weight="bold">C#</text>
    </svg>
  ),
  swift: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" fill="#F05138"/>
      <path d="M15.5 5.5L8.5 18.5" stroke="white" strokeWidth="2"/>
    </svg>
  ),
  http: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2L1 9V15L12 22L23 15V9L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 22V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M23 9L12 15L1 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
};

const RightPanel = ({ api, paramValues, baseUrl, handleParamChange, securitySchemes, activeScheme, setActiveScheme, dummyResponseMap, showAuthorization }) => {
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isTryItDisabled, setIsTryItDisabled] = useState(true);
  const [activeLanguage, setActiveLanguage] = useState(api?.examples ? Object.keys(api.examples)[0] : '');
  const [visibleLanguages, setVisibleLanguages] = useState(api?.examples ? Object.keys(api.examples) : []);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dynamicSnippets, setDynamicSnippets] = useState(api?.examples || {});
  const [responseSource, setResponseSource] = useState(null);
  const [activeExample, setActiveExample] = useState(null);

  const containerRef = useRef(null);

useEffect(() => {
    setResponse(null);
    setError(null);
    setLoading(false);
    setResponseSource(null);
    setActiveExample(null);
  }, [api]);
  
  useEffect(() => {
    if (!api || !api.parameters) {
      setIsTryItDisabled(true);
      return;
    }
  
    const allParamsValid = api.parameters.every(p => {
      // If a parameter is an array, we have special validation rules
      if (p.schema.type === 'array' && p.schema.items && p.schema.items.properties) {
        const arrayValues = paramValues[p.name] || [];
        
        // If the array itself is required, it must not be empty.
        if (p.required && arrayValues.length === 0) {
          return false;
        }
  
        // For every item in the array, its required fields must be filled.
        return arrayValues.every(item => {
          const requiredFields = p.schema.items.required || [];
          return requiredFields.every(field => {
            const value = item[field];
            return value !== undefined && value !== null && String(value).trim() !== '';
          });
        });
      }
  
      // For non-array parameters, they are valid if they are not required, or if they are required and have a value.
      if (p.required) {
        return paramValues[p.name] && String(paramValues[p.name]).trim() !== '';
      }
  
      // If not required and not an array, it's valid.
      return true;
    });
  
    setIsTryItDisabled(!allParamsValid);
  }, [api, paramValues]);

  useEffect(() => {
    const calculateVisibleLanguages = () => {
      if (containerRef.current && api) {
        const allLanguages = Object.keys(api.examples);
        const containerWidth = containerRef.current.offsetWidth;
        const buttonWidth = 80;
        const gap = 12;
        let maxVisible = Math.floor((containerWidth + gap) / (buttonWidth + gap));

        if (allLanguages.length > maxVisible) {
          maxVisible = maxVisible - 1;
        }
        
        setVisibleLanguages(allLanguages.slice(0, maxVisible));
      }
    };

    calculateVisibleLanguages();
    window.addEventListener('resize', calculateVisibleLanguages);

    return () => {
      window.removeEventListener('resize', calculateVisibleLanguages);
    };
  }, [api]);

  useEffect(() => {
    if(api) {
      const newSnippets = {
        curl: generateCurlSnippet(api, paramValues, securitySchemes, activeScheme, showAuthorization),
        javascript: generateJavascriptSnippet(api, paramValues, securitySchemes, activeScheme, showAuthorization),
        python: generatePythonSnippet(api, paramValues, securitySchemes, activeScheme, showAuthorization),
        ruby: generateRubySnippet(api, paramValues, securitySchemes, activeScheme, showAuthorization),
        c: generateCSnippet(api, paramValues, securitySchemes, activeScheme, showAuthorization),
        cpp: generateCppSnippet(api, paramValues, securitySchemes, activeScheme, showAuthorization),
        java: generateJavaSnippet(api, paramValues, securitySchemes, activeScheme, showAuthorization),
        shell: generateShellSnippet(api, paramValues, securitySchemes, activeScheme, showAuthorization),
        node: generateNodeSnippet(api, paramValues, securitySchemes, activeScheme, showAuthorization),
        php: generatePHPSnippet(api, paramValues, securitySchemes, activeScheme, showAuthorization),
        csharp: generateCSharpSnippet(api, paramValues, securitySchemes, activeScheme, showAuthorization),
        swift: generateSwiftSnippet(api, paramValues, securitySchemes, activeScheme, showAuthorization),
        http: generateHTTPSnippet(api, paramValues, securitySchemes, activeScheme, showAuthorization),
      };
      setDynamicSnippets(newSnippets);
    }
  }, [api, paramValues, baseUrl, securitySchemes, activeScheme, showAuthorization]);

  const handleLanguageSelect = (lang) => {
    setActiveLanguage(lang);
    setIsDropdownOpen(false);
  };

  const handleTryItOut = async () => {
    setLoading(true);
    setError(null);
    setResponse(null);
    setResponseSource('live');
    setActiveExample(null);

    let requestPath = api.path;
    const queryParams = {};
    const bodyParams = {};
    const headerParams = {};

    api.parameters.forEach((param) => {
      const value = paramValues[param.name];
      if (value) {
        if (param.in === 'path') {
          requestPath = requestPath.replace(`{${param.name}}`, encodeURIComponent(value));
        } else if (param.in === 'query') {
          queryParams[param.name] = value;
        } else if (param.in === 'body') {
          bodyParams[param.name] = value;
        } else if (param.in === 'header') {
           headerParams[param.name] = value;
        }
      }
    });

    if (showAuthorization && activeScheme && securitySchemes[activeScheme]) {
      const schemeDetails = securitySchemes[activeScheme];
      const value = paramValues[schemeDetails.name];
      if (value) {
        if (schemeDetails.in === 'query') {
          queryParams[schemeDetails.name] = value;
        } else if (schemeDetails.in === 'header') {
          let token = value;
          if (schemeDetails.name.toLowerCase() === 'authorization' && !token.toLowerCase().startsWith('bearer ')) {
            token = `Bearer ${token}`;
          }
          headerParams[schemeDetails.name] = token;
        }
      }
    }

    try {
      const result = await axios({
        method: api.method,
        url: `${api.baseUrl}${requestPath}`,
        params: queryParams,
        headers: headerParams,
        data: Object.keys(bodyParams).length > 0 ? bodyParams : undefined,
      });
      setResponse({
        status: result.status,
        data: result.data,
      });
    } catch (err) {
      setError({
        status: err.response?.status,
        data: err.response?.data || { message: err.message },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExampleSelect = (status, example) => {
    setResponse({
      status: status,
      data: example.value
    });
    setActiveExample(example);
    setResponseSource('example');
    setError(null);
  };

  return (
    <div className={styles.rightPanel}>
      {loading && <Spinner />}
      
      {!api ? (
        <div className={styles.rightPanelSection}>
          
        </div>
      ) : (
        <>
          <div className={styles.rightPanelSection}>
            <h5 className={styles.rightPanelSectionHeader}>LANGUAGE</h5>
            <div className={styles.languageButtonsContainer} ref={containerRef}>
              <div className={styles.languageButtons}>
                {visibleLanguages.map((lang) => (
                  <div
                    key={lang}
                    className={`${styles.langButton} ${activeLanguage === lang ? styles.active : ''}`}
                    onClick={() => handleLanguageSelect(lang)}
                  >
                    {languageIcons[lang]}
                    <span className={styles.langName}>{lang}</span>
                  </div>
                ))}
                {api.examples && Object.keys(api.examples).length > visibleLanguages.length && (
                  <div className={styles.dropdownContainer}>
                    <button className={styles.dropdownToggle} onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                      <svg className={styles.dropdownToggleIcon} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="1" />
                        <circle cx="19" cy="12" r="1" />
                        <circle cx="5" cy="12" r="1" />
                      </svg>
                    </button>
                    {isDropdownOpen && (
                      <div className={styles.dropdownMenu}>
                        {Object.keys(api.examples)
                          .filter((lang) => !visibleLanguages.includes(lang))
                          .map((lang) => (
                            <div
                              key={lang}
                              className={styles.dropdownItem}
                              onClick={() => handleLanguageSelect(lang)}
                            >
                              {languageIcons[lang]}
                              <span className={styles.langName}>{lang}</span>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {showAuthorization && (
              <div className={styles.sectionContent}>
                <Authorization
                  key={api.id} // Force re-mount on API change
                  securitySchemes={securitySchemes}
                  paramValues={paramValues}
                  handleParamChange={handleParamChange}
                  activeScheme={activeScheme}
                  setActiveScheme={setActiveScheme}
                />
              </div>
            )}
          </div>
          <CodeSnippet
            examples={dynamicSnippets}
            language={activeLanguage}
            hasScroll={true}
          />
      
          <div className={`${styles.rightPanelSection} ${styles.fetchRequestSection}`}>
            <h5 className={styles.rightPanelSectionHeader}>FETCH REQUEST</h5>
            <button onClick={handleTryItOut} disabled={loading || isTryItDisabled} className={styles.tryItOutButton}>
              {loading ? 'Executing...' : 'Try It!'}
            </button>
          </div>

          <Response
            key={api?.id}
            response={response}
            error={error}
            loading={loading}
            responseSource={responseSource}
            activeExample={activeExample}
            api={api}
            onExampleSelect={handleExampleSelect}
            dummyResponseMap={dummyResponseMap}
          />
        </>
      )}
    </div>
  );
};

export default RightPanel;